import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import type { Termin } from "../../lib/types";
import { bezugLink, bezugRow, bezugRowOben, durBadge, ortLink, timeCap, timeField, timeLg } from "./planer-styles";
import { mapsUrl } from "./planer-utils";
import { Zeitfeld } from "./Zeitfeld";

/** Ort des Projekts als Maps-Link. Ohne hinterlegten Ort erscheint nichts. */
export function OrtLink({ ort, stil }: { ort: string; stil?: CSSProperties }) {
  if (!(ort || "").trim()) return null;
  return (
    <a
      href={mapsUrl(ort)}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      style={{ ...ortLink, ...stil }}
      title="Auf Google Maps öffnen"
    >
      <span aria-hidden="true">📍</span>{ort}
    </a>
  );
}

// ── Projekt · Kunde · Ort als Sprungziele ──────────────────────────────────
/**
 * Vorher standen Projekt, Kunde und Ort nur als Text am Termin. Wer von dort
 * ins Projekt oder zum Kunden wollte, musste in der Seitenleiste neu suchen —
 * und der Ort war überhaupt nur im Kopf des geöffneten Termins zu sehen.
 */
export function TerminBezuege({ t, oben, ohneOrt, onKunde }: {
  t: Termin; oben?: boolean; ohneOrt?: boolean;
  /** Kunde als Blatt von rechts zeigen, statt die Seite zu wechseln (14.10.2026). */
  onKunde?: (kundeId: string) => void;
}) {
  /* Im geöffneten Termin steht der Einsatzort genau einmal — in der Ortswahl, wo er
     sich auch ändern lässt (14.10.2026). Dort also keine zweite Ortszeile. */
  const ort = ohneOrt ? "" : (t.projekt_ort || "").trim();
  if (!t.projekt_id && !t.kunde_id && !ort) return null;
  return (
    <div style={oben ? bezugRowOben : bezugRow}>
      {t.projekt_id ? (
        <Link to={`/projekte/${t.projekt_id}`} style={bezugLink} onClick={(e) => e.stopPropagation()}>
          {oben ? "Projekt öffnen" : (t.projekt_title || "Projekt")} ↗
        </Link>
      ) : null}
      {t.kunde_id ? (
        onKunde ? (
          <button type="button" className="pl-bezug-knopf" style={bezugLink} onClick={(e) => { e.stopPropagation(); onKunde(t.kunde_id); }}>
            {t.kunde_name || "Kunde"} ↗
          </button>
        ) : (
          <Link to={`/kunden?kunde=${t.kunde_id}`} style={bezugLink} onClick={(e) => e.stopPropagation()}>
            {t.kunde_name || "Kunde"} ↗
          </Link>
        )
      ) : (t.kunde_name ? <span style={{ fontSize: 12.5, color: "var(--vg-text-3)" }}>{t.kunde_name}</span> : null)}
      <OrtLink ort={ort} />
      {/* Abweichender Ort: wo man hinfaehrt, soll man nicht erst aufklappen muessen. */}
      {!ohneOrt && t.einsatzort_quelle && t.einsatzort_name ? (
        <span style={{ fontSize: 12, color: "var(--vg-text-3)" }}>{t.einsatzort_quelle.startsWith("lieferant:") ? `bei ${t.einsatzort_name}` : t.einsatzort_name}</span>
      ) : null}
    </div>
  );
}

// ── Von–Bis eines Termins ──────────────────────────────────────────────────
/**
 * Dieselbe Zeile steht im Entwurf und im bestehenden Termin, deshalb eine
 * eigene Komponente. Die Felder tragen jetzt eine Beschriftung: ohne sie war
 * nicht erkennbar, welches der Beginn ist — und sie sind breit genug, dass
 * Chrome neben der Uhrzeit noch sein Picker-Symbol unterbringt, statt „08:30"
 * auf „08:" zu kappen.
 */
export function Zeitspanne({ von, bis, onVon, onBis, dauer }: {
  /** Ohne Angabe kein Dauer-Kärtchen — im geöffneten Termin steht die Dauer schon im Kopf. */
  von: string; bis: string; onVon: (v: string) => void; onBis: (v: string) => void; dauer?: string;
}) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 10, flexWrap: "wrap" }}>
      <label style={timeField}>
        <span style={timeCap}>VON</span>
        <Zeitfeld value={von} onChange={onVon} stil={timeLg} groesse="lg" label="Beginn" ariaLabel="Beginn" />
      </label>
      <span style={{ color: "var(--vg-text-3)", paddingBottom: 10 }}>—</span>
      <label style={timeField}>
        <span style={timeCap}>BIS</span>
        <Zeitfeld value={bis} onChange={onBis} stil={timeLg} groesse="lg" label="Ende" ariaLabel="Ende" />
      </label>
      {dauer && <span style={{ ...durBadge, marginBottom: 2 }}>{dauer}</span>}
    </div>
  );
}
